/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import {
  Claim,
  ClaimStatusGroup,
  CLAIM_STATUS_GROUPS,
  claimStatusGroup,
  claimStatusGroupLabelKey,
  useClaimMutations,
  useClaimsQuery,
} from 'src/services/claim';
import { useQuickFilterQuery } from 'src/services/utility';
import { DataView } from '../components/ui/TableOverview/DataView';
import NoDataPlaceholder from '../components/layouts/NoDataPlaceholder/NoDataPlaceholder';
import { useDataViewController } from '../hooks/useDataViewController';
import { SearchModels } from '../components/ui/TableOverview/utils/SearchFieldRegistry';
import { OverlayNavProvider } from '../providers/OverlayNavProvider';
import { getFormattedDay } from './CasesOverview';

const BASE_PATH = '/cases/claims';

// The list + tabs show the consolidated status group, not the raw status.
const GROUP_BADGE: Record<ClaimStatusGroup, BadgeType> = {
  submitted: BadgeType.Neutral,
  documents_missing: BadgeType.Error,
  at_carrier: BadgeType.Warning,
  closed: BadgeType.Successful,
};

function ClaimsOverview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const controller = useDataViewController();
  const { deleteMutation } = useClaimMutations();
  const { data: quickFilters } = useQuickFilterQuery('df.claim');
  const [entityIds, setEntityIds] = useState<(string | number)[]>([]);

  const typeLabel = (type: string) => t(`claims.type_${type}`, { defaultValue: type });
  const groupLabel = (status: string) => t(claimStatusGroupLabelKey(status), { defaultValue: status });

  const columnHelper = createColumnHelper<Claim>();
  const columns: ColumnDef<Claim, any>[] = [
    columnHelper.accessor(row => row.name, {
      id: 'name',
      size: 120,
      meta: { label: t('claims.col_number', { defaultValue: 'No.' }) },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => <span className="typo-text-14--bold text-full">{info.getValue()}</span>,
    }),
    columnHelper.accessor(row => row.timestamp, {
      id: 'create_date',
      size: 100,
      meta: { label: t('claims.col_date', { defaultValue: 'Date' }) },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const v = info.getValue() as string | undefined;
        return <span className="typo-text-14 text-faded">{v ? getFormattedDay(new Date(v)) : ''}</span>;
      },
    }),
    columnHelper.accessor(row => row.claim_type, {
      id: 'claim_type',
      size: 120,
      meta: { label: t('claims.col_type', { defaultValue: 'Type' }) },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <Badge className="max-w-min" label={typeLabel(info.getValue() as string)} expanded type={BadgeType.Neutral2} />
      ),
    }),
    columnHelper.accessor(row => row.carrier, {
      id: 'carrier_id',
      size: 120,
      meta: { label: t('claims.col_carrier', { defaultValue: 'Carrier' }) },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => <span className="typo-text-14 text-faded">{(info.getValue() as string) || '–'}</span>,
    }),
    columnHelper.accessor(row => row.tracking_number, {
      id: 'tracking_number',
      size: 140,
      meta: { label: t('claims.col_tracking', { defaultValue: 'Tracking number' }) },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => <span className="typo-text-14 text-faded">{(info.getValue() as string) || '–'}</span>,
    }),
    columnHelper.accessor(row => row.status, {
      id: 'status',
      size: 120,
      meta: { label: t('claims.col_status', { defaultValue: 'Status' }) },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const status = info.getValue() as string;
        return (
          <Badge
            className="max-w-min"
            label={groupLabel(status)}
            expanded
            type={GROUP_BADGE[claimStatusGroup(status)]}
          />
        );
      },
    }),
    columnHelper.accessor(row => row.goods_value, {
      id: 'goods_value',
      size: 100,
      meta: { label: t('claims.col_goods_value', { defaultValue: 'Goods value' }) },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const v = info.getValue() as number | undefined;
        return <span className="typo-text-14 text-faded">{v != null ? `€${v}` : '–'}</span>;
      },
    }),
  ];

  // Group counts are summed from the per-status quick filters the backend returns.
  const groupCount = (statuses: string[]) =>
    (quickFilters ?? []).filter(f => statuses.includes(f.key)).reduce((sum, f) => sum + (f.value ?? 0), 0);

  const tabs = [
    {
      label: t('general.all'),
      key: 'all',
      value: (quickFilters ?? []).reduce((sum, f) => sum + (f.value ?? 0), 0),
    },
    ...CLAIM_STATUS_GROUPS.map(group => ({
      label: t(`claims.group_${group.key}`),
      key: group.key,
      value: groupCount(group.statuses),
    })),
  ];

  return (
    <OverlayNavProvider entityIds={entityIds} basePath={BASE_PATH}>
      <DataView<Claim>
        onDataLoaded={items => setEntityIds(items.map(i => i.id).filter((id): id is number => id != null))}
        filterModelId="df.claim"
        controller={controller}
        modelKey={SearchModels.Claim}
        headline={t('claims.title', { defaultValue: 'Claims' })}
        emptyState={<NoDataPlaceholder headline={t('general.empty_claims')} />}
        columns={columns}
        useDataQuery={useClaimsQuery}
        tabs={tabs}
        getDomainForTab={(tabKey, index) => {
          if (index === 0) return '[]';
          const group = CLAIM_STATUS_GROUPS.find(g => g.key === tabKey);
          const statuses = group?.statuses ?? [tabKey];
          return `[("status", "in", [${statuses.map(s => `"${s}"`).join(', ')}])]`;
        }}
        actions={[
          {
            title: t('claims.new', { defaultValue: 'New claim' }),
            icon: 'plus',
            onExecute: () => navigate(`${BASE_PATH}/create`),
            showInToolbar: true,
          },
          {
            title: t('general.view_details'),
            icon: 'edit',
            isRowClickAction: true,
            onExecute: selected => {
              if (selected.length > 0) navigate(`${BASE_PATH}/${selected[0].id}`);
            },
            showInContextMenu: true,
          },
          {
            title: t('claims.delete', { defaultValue: 'Delete' }),
            titlePlural: t('claims.delete', { defaultValue: 'Delete' }),
            icon: 'close',
            isError: true,
            showInContextMenu: true,
            bulkSupport: true,
            isDisabled: selected => selected.some(s => s.status !== 'submitted'),
            toast: {
              renderSuccessMessage: () => t('claims.delete_success', { defaultValue: 'Claim deleted.' }),
              renderErrorMessage: () => t('claims.delete_failed', { defaultValue: 'Could not delete the claim.' }),
            },
            onExecute: async selected => {
              await Promise.all(
                selected
                  .filter(s => s.id != null)
                  .map(s => deleteMutation.mutateAsync({ params: { path: { claim_id: s.id as number } } })),
              );
            },
            confirmSettings: {
              renderTitle: () => t('claims.delete', { defaultValue: 'Delete claim' }),
              renderContent: () =>
                t('claims.delete_text', { defaultValue: 'Are you sure you want to delete this claim?' }),
              confirmLabel: t('general.confirm'),
              cancelLabel: t('general.cancel'),
            },
          },
        ]}
      />
    </OverlayNavProvider>
  );
}

export default ClaimsOverview;
